import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

function TrendCard({ label, thisWeek, lastWeek }) {
  const diff    = thisWeek - lastWeek;
  const pct     = lastWeek === 0 ? 100 : Math.round((diff / lastWeek) * 100);
  const up      = diff >= 0;
  const color   = up ? '#00cc88' : '#ff4444';
  const arrow   = up ? '↑' : '↓';

  return (
    <div style={{
      background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
      padding: 20, flex: 1, minWidth: 140
    }}>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{thisWeek}</div>
      <div style={{ fontSize: 13, color, marginTop: 4 }}>
        {arrow} {Math.abs(pct)}% vs last week
      </div>
      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Last week: {lastWeek}</div>
    </div>
  );
}

export default function Growth() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const now       = new Date();
      const tz        = 'Europe/Dublin';

      const weekAgo   = new Date(now.getTime() - 7  * 86400000);
      const twoWeeksAgo = new Date(now.getTime() - 14 * 86400000);
      const monthAgo  = new Date(now.getTime() - 30 * 86400000);

      // Bookings this week
      const { count: bookingsThisWeek } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'booked')
        .gte('created_at', weekAgo.toISOString());

      // Bookings last week
      const { count: bookingsLastWeek } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'booked')
        .gte('created_at', twoWeeksAgo.toISOString())
        .lte('created_at', weekAgo.toISOString());

      // New students this week
      const { count: studentsThisWeek } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekAgo.toISOString());

      // New students last week
      const { count: studentsLastWeek } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', twoWeeksAgo.toISOString())
        .lte('created_at', weekAgo.toISOString());

      // Cancellation rate this month
      const { count: totalThisMonth } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', monthAgo.toISOString());

      const { count: cancelledThisMonth } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'cancelled')
        .gte('created_at', monthAgo.toISOString());

      const cancelRate = totalThisMonth > 0
        ? Math.round((cancelledThisMonth / totalThisMonth) * 100)
        : 0;

      // Bookings per day for last 14 days (chart)
      const { data: recentBookings } = await supabase
        .from('bookings')
        .select('created_at')
        .gte('created_at', twoWeeksAgo.toISOString())
        .order('created_at', { ascending: true });

      const chartMap = {};
      for (let i = 13; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400000);
        const key = d.toLocaleDateString('en-CA', { timeZone: tz });
        chartMap[key] = 0;
      }
      for (const b of (recentBookings || [])) {
        const key = new Date(b.created_at).toLocaleDateString('en-CA', { timeZone: tz });
        if (chartMap[key] !== undefined) chartMap[key]++;
      }

      const chartData = Object.entries(chartMap).map(([date, count]) => ({
        date: date.slice(5),
        bookings: count
      }));

      setData({
        bookingsThisWeek,  bookingsLastWeek,
        studentsThisWeek,  studentsLastWeek,
        cancelRate,        chartData
      });
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ color: '#888', padding: 20 }}>Loading...</div>;

  return (
    <div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
        Growth
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <TrendCard label="Bookings"     thisWeek={data.bookingsThisWeek} lastWeek={data.bookingsLastWeek} />
        <TrendCard label="New students" thisWeek={data.studentsThisWeek} lastWeek={data.studentsLastWeek} />
        <div style={{
          background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
          padding: 20, flex: 1, minWidth: 140
        }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>Cancellation rate</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: data.cancelRate > 20 ? '#ff4444' : '#fff' }}>
            {data.cancelRate}%
          </div>
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>Last 30 days</div>
        </div>
      </div>

      {/* Bookings chart */}
      <div style={{
        background: '#1a1a1a', border: '1px solid #2a2a2a',
        borderRadius: 12, padding: 20
      }}>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Bookings — last 14 days</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.chartData}>
            <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 11 }} />
            <YAxis tick={{ fill: '#666', fontSize: 11 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 8 }}
              labelStyle={{ color: '#888' }}
            />
            <Line
              type="monotone" dataKey="bookings"
              stroke="#6c47ff" strokeWidth={2} dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}